import type { ReactNode } from "react";

interface PlatformPageHeaderProps {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** A back-navigation link/button rendered above the eyebrow line. */
  back?: ReactNode;
  /** A leading avatar/logo block rendered beside the title (e.g. an editable org logo). */
  media?: ReactNode;
  /** An inline row rendered under the title (e.g. a slug and a status badge). */
  meta?: ReactNode;
}

/**
 * Shared header for the platform pages, including detail pages that carry a
 * back link, an editable media slot, and a meta row. Presentation only —
 * every page keeps its own data hooks, table, filters, tabs and dialogs
 * below this; callers pass in their existing interactive elements as-is.
 */
export function PlatformPageHeader({ eyebrow, title, subtitle, actions, back, media, meta }: PlatformPageHeaderProps) {
  return (
    <header className="mb-7 border-b border-border pb-6">
      {back ? <div className="mb-4">{back}</div> : null}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          {media}
          <div className="min-w-0">
            <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.17em] text-teal">{eyebrow}</p>
            <h1 className="mt-1 break-words font-heading text-3xl font-bold text-text">{title}</h1>
            {meta ? <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div> : null}
            {subtitle ? <p className="mt-2 max-w-2xl font-body text-sm leading-6 text-text-muted">{subtitle}</p> : null}
          </div>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}
